import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert} from 'Asserts';
import {
  BASE_FILTER_FORM_PROP_NAMES,
  BaseFilterFormProps,
  FilterStyle,
} from 'search/filters/base/FilterFormConstants';
import OperatorDropdown from 'search/filters/base/OperatorDropdown';
import PrimaryFilterFormSkeleton from 'search/filters/base/PrimaryFilterFormSkeleton';
import SecondaryFilterFormSkeleton from 'search/filters/base/SecondaryFilterFormSkeleton';
import {
  FullTextFilter,
  isFullTextFilter,
  isMultiValueFilter,
  isSingleValueFilter,
  MultiValueFilter,
  Operator,
  SingleValueFilter,
} from 'search/SearchConstants';
import {ellipsisText} from 'styles/Utilities';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import pickDefined from 'util/pickDefined';

export function getFilterText(
  options: Array<OptionData>,
  filter: SingleValueFilter | MultiValueFilter | FullTextFilter
): string | null {
  const value = filter.value;
  if (isMultiValueFilter(filter)) {
    const selectedOptions = options.filter((op) => value!.includes(op.value as string));
    return selectedOptions.length ? selectedOptions.map(({name}) => name).join(', ') : null;
  } else if (isFullTextFilter(filter) || isSingleValueFilter(filter)) {
    const selectedOption = _.findWhere(options, {value});
    return selectedOption && value ? (selectedOption.name as string) : null;
  } else {
    throw new Error('Unexpected filter');
  }
}

export function prefixedToggleText(
  prefix: string,
  optionDatas: Array<OptionData>,
  filter: SingleValueFilter | MultiValueFilter
): string {
  const filterText = getFilterText(optionDatas, filter);
  return filterText ? `${prefix}: ${filterText}` : prefix;
}

export type DropdownFilterFormProps = BaseFilterFormProps & {
  filterStyle: FilterStyle;
  getMatchingOptionDatas?: (query: string, optionDatas: Array<OptionData>) => Array<OptionData>;
  isSearchable?: boolean;
  onChangeValue: (value: Array<string | null> | string | null | object, selectedValue?: string) => void;
  onRemove?: () => void;
  optionDatas: Array<OptionData>;
  renderOptionContent?: (optionData: OptionData) => JSX.Element;
  onChangeOperator?: (value: Operator) => void;
  operators?: Array<Operator>;
  selectedOperator?: Operator;
  shouldEllipsizeOptions?: boolean;
  dataTestAttrs?: DataTestAttrs;
} & (IsMulti | NotMulti);
interface IsMulti {
  isMulti: true;
  value: Array<string>;
}

interface NotMulti {
  isMulti: false;
  value: Array<string> | string | null | object;
}

class DropdownFilterForm extends React.Component<DropdownFilterFormProps> {
  _handleSelect = (selectedOptionValue: string): void => {
    if (this.props.isMulti) {
      const {value} = this.props;
      if (value == null) {
        throw new Error('Expected value to be non-null for isMulti');
      }
      this.props.onChangeValue(
        value.includes(selectedOptionValue)
          ? // eslint-disable-next-line no-restricted-syntax
            _.without(value, selectedOptionValue)
          : this.props.optionDatas
              .map((o) => o.value)
              .filter((o) => o != null && [...value, selectedOptionValue].includes(o)),
        selectedOptionValue
      );
    } else {
      this.props.onChangeValue(selectedOptionValue);
    }
  };

  override render(): JSX.Element | null {
    assert(!(this.props.isMulti && this.props.value == null), 'Expected value to be non-null for isMulti');
    const skeletonProps = pickDefined(this.props, ...BASE_FILTER_FORM_PROP_NAMES);
    const visibleOptionDatas = this.props.optionDatas.filter((optionData) => !optionData.hidden);
    // Display operator dropdown if there are operators to choose from
    let operatorDropdown = null;
    if (this.props.operators != null && this.props.operators.length > 1) {
      assert(
        this.props.onChangeOperator != null,
        'Expected onChangeOperator to be defined when more than one operator is provided'
      );
      assert(
        this.props.selectedOperator != null,
        'Expected selectedOperator to be defined when more than one operator is provided'
      );
      operatorDropdown =
        this.props.onChangeOperator != null && this.props.selectedOperator != null ? (
          <OperatorDropdown
            buttonSize={this.props.buttonSize}
            onChangeOperator={this.props.onChangeOperator}
            operators={this.props.operators}
            selectedOperator={this.props.selectedOperator}
          />
        ) : null;
    }
    const menu = this.props.isSearchable ? (
      <SearchableMenu
        scrollable={true}
        getMatchingOptionDatas={this.props.getMatchingOptionDatas}
        height={MenuHeight.MEDIUM}
        optionDatas={visibleOptionDatas}
        onSelect={this._handleSelect}
        renderOptionContent={this.props.renderOptionContent}
        selectedValue={this.props.value}
        width={MenuWidth.FULL}
        isMulti={this.props.isMulti}
        skipIconOverride={true}
      />
    ) : (
      <Menu
        scrollable={true}
        height={MenuHeight.MEDIUM}
        isMulti={this.props.isMulti}
        value={this.props.value}
        width={MenuWidth.FULL}
      >
        {visibleOptionDatas.map((optionData) => (
          <Option
            className={optionData.className}
            key={optionData.name || optionData.value}
            onSelect={this._handleSelect}
            shouldEllipsize={this.props.shouldEllipsizeOptions ?? true}
            value={optionData.value}
            icon={optionData.icon}
            iconClassName={optionData.iconClassName}
            dataTestAttrs={optionData.dataTestAttrs}
          >
            {optionData.name}
          </Option>
        ))}
      </Menu>
    );
    let text: string;
    if (this.props.isLoading) {
      text = 'Loading...';
    } else if (this.props.isMulti) {
      if (this.props.value == null) {
        throw new Error('Expected value to be non-null for isMulti');
      }
      const selectedOptions = this.props.optionDatas.filter((option) =>
        // @ts-expect-error: already checking for null value above
        this.props.value.includes(option.value)
      );
      text = selectedOptions.length
        ? selectedOptions.map((option) => option.name as string).join(', ')
        : 'Any';
    } else {
      // eslint-disable-next-line no-restricted-syntax
      const selectedOption = _.find(this.props.optionDatas, (optionData) =>
        isEqual(optionData.value, this.props.value)
      );
      text = selectedOption ? (selectedOption.name as string) : 'Any';
    }
    if (this.props.filterStyle === FilterStyle.PRIMARY) {
      return (
        <PrimaryFilterFormSkeleton
          {...skeletonProps}
          tooltipTitle={text}
          closeOnInsideClick={!this.props.isMulti}
          content={menu}
          dropdownToggleText={this.props.dropdownToggleText}
          dataTestAttrs={
            this.props.dataTestAttrs ?? {component: 'DropdownFilterForm', element: 'dropdownButton'}
          }
        />
      );
    } else {
      if (!this.props.onRemove) {
        throw new Error('Expected this.props.onRemove to be present when filterStyle is SECONDARY');
      }
      const dropdown = (
        <TooltipWrapper tooltipTitle={!this.props.isLoading ? text : null}>
          <Dropdown closeOnInsideClick={!this.props.isMulti} menu={menu}>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              fullWidth={true}
              isDropdown={true}
              size={this.props.buttonSize}
              text={text}
              textClassName={css(styles.buttonText)}
              disabled={this.props.isDisabled}
              dataTestAttrs={{component: 'DropdownFilterForm', element: 'dropdownButton'}}
            />
          </Dropdown>
        </TooltipWrapper>
      );
      const nodeGrid = operatorDropdown != null ? [[operatorDropdown], [dropdown]] : [[dropdown]];
      return (
        <SecondaryFilterFormSkeleton
          {...skeletonProps}
          nodeGrid={nodeGrid}
          onChangeFilterKey={this.props.onChangeFilterKey}
          selectedFilterKey={this.props.selectedFilterKey}
          onRemove={this.props.onRemove}
        />
      );
    }
  }
}

export default DropdownFilterForm;

const styles = StyleSheet.create({
  buttonText: {
    maxWidth: 120,
    ...ellipsisText,
  },
});
